/**
 * MonthNavigation Component
 *
 * Displays month navigation controls: previous/next month buttons,
 * a formatted month/year header, and a "Hoy" button to reset to the current month.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4
 * - Requirement 3.1: Navigation controls adjacent to the month/year header.
 * - Requirement 3.2: Previous month button navigates to the preceding month.
 * - Requirement 3.3: Next month button navigates to the following month.
 * - Requirement 3.4: "Hoy" button navigates back to the current month.
 */

import React from 'react';
import { tokens } from '../../../design-system/tokens';
import { formatMonthHeader } from '../calendarView.utils';

// ============================================
// TYPES
// ============================================

export interface MonthNavigationProps {
  /** Current displayed year */
  year: number;
  /** Current displayed month (1-12) */
  month: number;
  /** Callback when previous month button is clicked */
  onPrev: () => void;
  /** Callback when next month button is clicked */
  onNext: () => void;
  /** Callback when "Hoy" button is clicked */
  onToday: () => void;
}

// ============================================
// STYLES
// ============================================

const navButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2rem',
  height: '2rem',
  border: `1px solid ${tokens.colors.neutral[300]}`,
  borderRadius: tokens.borderRadius.md,
  backgroundColor: tokens.colors.neutral[50],
  color: tokens.colors.neutral[700],
  fontSize: tokens.typography.fontSize.base,
  fontWeight: tokens.typography.fontWeight.medium,
  cursor: 'pointer',
  transition: `background-color ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}`,
  lineHeight: 1,
  padding: 0,
};

const todayButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
  border: `1px solid ${tokens.colors.neutral[300]}`,
  borderRadius: tokens.borderRadius.md,
  backgroundColor: tokens.colors.neutral[50],
  color: tokens.colors.neutral[700],
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  cursor: 'pointer',
  transition: `background-color ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}`,
};

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * MonthNavigation — Header with month/year display and navigation controls.
 *
 * Layout:
 * - Left side: ← button, month/year header, → button
 * - Right side: "Hoy" button
 *
 * @example
 * ```tsx
 * <MonthNavigation
 *   year={2025}
 *   month={6}
 *   onPrev={() => setMonth(prev)}
 *   onNext={() => setMonth(next)}
 *   onToday={() => setMonth(today)}
 * />
 * ```
 */
export function MonthNavigation({ year, month, onPrev, onNext, onToday }: MonthNavigationProps) {
  const header = formatMonthHeader(year, month);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: tokens.spacing[4],
      }}
    >
      {/* Left side: navigation arrows + month/year header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacing[2],
        }}
      >
        <button
          type="button"
          onClick={onPrev}
          aria-label="Mes anterior"
          style={navButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[200];
            e.currentTarget.style.borderColor = tokens.colors.neutral[400];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
            e.currentTarget.style.borderColor = tokens.colors.neutral[300];
          }}
        >
          ←
        </button>

        <h2
          style={{
            margin: 0,
            fontSize: tokens.typography.fontSize.xl,
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.neutral[800],
            lineHeight: tokens.typography.lineHeight.tight,
            minWidth: '10rem',
            textAlign: 'center',
          }}
        >
          {header}
        </h2>

        <button
          type="button"
          onClick={onNext}
          aria-label="Mes siguiente"
          style={navButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[200];
            e.currentTarget.style.borderColor = tokens.colors.neutral[400];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
            e.currentTarget.style.borderColor = tokens.colors.neutral[300];
          }}
        >
          →
        </button>
      </div>

      {/* Right side: "Hoy" button */}
      <button
        type="button"
        onClick={onToday}
        aria-label="Ir al mes actual"
        style={todayButtonStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = tokens.colors.neutral[200];
          e.currentTarget.style.borderColor = tokens.colors.neutral[400];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
          e.currentTarget.style.borderColor = tokens.colors.neutral[300];
        }}
      >
        Hoy
      </button>
    </div>
  );
}

export default MonthNavigation;
